import { resolveProfilePictureDisplayUrl } from "../../../utils/profilePictureUrl";
import type { NetworkingMapResponse } from "./networkingMapTypes";

const AVATAR_PALETTE = [
  "#1F4E79",
  "#2E6B60",
  "#8E3B36",
  "#4A5568",
  "#7B5E3B",
  "#3C6E71",
];

/**
 * Cytoscape splits `background-image` values on commas. Cloudflare CDN URLs embed
 * commas in transform params (w=120,h=120,...), so encode them for the graph only.
 */
export function encodeAvatarUrlForCytoscape(avatarUrl: string): string {
  const trimmed = avatarUrl.trim();
  if (!trimmed || trimmed.startsWith("data:") || !trimmed.includes(",")) {
    return trimmed;
  }

  return trimmed.replace(/,/g, "%2C");
}

/**
 * Cytoscape draws node images on a canvas, so a data-URI avatar is used whenever
 * there is no photo to load.
 */
export function buildInitialsAvatar(name: string, index = 0): string {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  const background = AVATAR_PALETTE[index % AVATAR_PALETTE.length];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect width="120" height="120" fill="${background}"/><text x="60" y="60" fill="#ffffff" font-family="Helvetica, Arial, sans-serif" font-size="44" font-weight="600" text-anchor="middle" dominant-baseline="central">${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function resolveNodeAvatarUrl(
  personId: string | undefined,
  apiAvatar?: string,
): string {
  const fromApi = apiAvatar?.trim();
  if (fromApi) {
    return fromApi;
  }

  return resolveProfilePictureDisplayUrl(personId, undefined)?.trim() ?? "";
}

function avatarImageLoads(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = url;
  });
}

/** Prepare graph avatars: comma-safe CDN URLs for Cytoscape, initials when no photo. */
export function withFallbackAvatars(
  data: NetworkingMapResponse,
): NetworkingMapResponse {
  return {
    ...data,
    nodes: data.nodes.map((node, index) => {
      const resolved = resolveNodeAvatarUrl(node.data.id, node.data.avatar);
      const avatar = resolved
        ? encodeAvatarUrlForCytoscape(resolved)
        : buildInitialsAvatar(node.data.name, index);

      return {
        data: {
          ...node.data,
          avatar,
        },
      };
    }),
  };
}

/** Swap URLs that 404 / fail to load for initials so nodes are not left gray. */
export async function withVerifiedAvatars(
  data: NetworkingMapResponse,
): Promise<NetworkingMapResponse> {
  const nodes = await Promise.all(
    data.nodes.map(async (node, index) => {
      const avatar = node.data.avatar?.trim() ?? "";
      if (!avatar || avatar.startsWith("data:")) {
        return node;
      }

      const probeUrl = avatar.includes("%2C")
        ? avatar.replace(/%2C/g, ",")
        : avatar;
      const loaded = await avatarImageLoads(probeUrl);
      if (loaded) {
        return node;
      }

      return {
        data: {
          ...node.data,
          avatar: buildInitialsAvatar(node.data.name, index),
        },
      };
    }),
  );

  return { ...data, nodes };
}
